import { format } from '@wolfdanepug/js-utils'
import type { StepSection, StepSegment } from '../types'
import { Logger } from '@wolfdanepug/common-react'
import Activity from '../components/Activity'
import Basics from '../components/Basics'
import Body from '../components/Body'
import Diet from '../components/Diet'
import Intro from '../components/Intro'
import Mealtime from '../components/Mealtime'
import UserInfo from '../components/UserInfo'

const buildPath = (name: string, segment: string) => {
	let path = '/dogs/[name]'
	segment && (path += '/[segment]')
	return format(path, { name, segment })
}

const dogRoute =
	(slug: string = '') =>
	({ data }: any) => {
		Logger.log('dogRoute', slug, data)
		const key = data?.dogs?.[data.activeDogContext]
		if (!key) return '/'
		const dog = data?.dog?.[key]
		if (!dog) return '/'
		return buildPath(dog.slug, slug)
	}

/**
 * Segment Creator
 * @param {Partial<StepSegment>} step
 * @returns {StepSegment} segment
 */
const segment = (step: Partial<StepSegment>): StepSegment => ({
	// saveData: saveOnboardingData,
	enabled: true,
	...step
})

/**
 * My Info Segment
 */
const segmentMyInfo: StepSegment = segment({
	name: 'my-info',
	uri: '/',
	component: UserInfo,
	nextStep: 'house-dogs'
})

/**
 * My Dogs Segment
 */
const segmentMyDogs: StepSegment = segment({
	name: 'house-dogs',
	uri: '/dogs',
	component: Intro,
	nextStep: 'dog-info',
	beforeSaveData: ({ data }: any) => ({
		...data,
		numberOfDogs: parseInt(data.numberOfDogs),
		selected__dog: parseInt(data.selected__dog)
	})
})

/**
 * Dog Info Segment
 */
const segmentDogInfo: StepSegment = segment({
	name: 'dog-info',
	uri: dogRoute(),
	component: Basics,
	nextStep: 'dog-body',
	beforeSaveData: ({ data }: any) => ({
		...data,
		dog: {
			...data.dog,
			age: parseInt(data.dog.age)
		}
	})
})

/**
 * Dog Body Segment
 */
const segmentDogBody: StepSegment = segment({
	name: 'dog-body',
	slug: 'body',
	uri: dogRoute('body'),
	component: Body,
	nextStep: 'dog-activity',
	beforeSaveData: ({ data }: any) => ({
		...data,
		dog: {
			...data.dog,
			weight: parseInt(data.dog.weight)
		}
	})
})

/**
 * Dog Mealtime Segment
 */
const segmentDogMealtime: StepSegment = segment({
	name: 'dog-mealtime',
	slug: 'mealtime',
	uri: dogRoute('mealtime'),
	component: Mealtime,
	nextStep: 'dog-diet',
	enabled: false
})

/**
 * Dog Activity Segment
 */
const segmentDogActivity: StepSegment = segment({
	name: 'dog-activity',
	slug: 'activity',
	uri: dogRoute('activity'),
	component: Activity,
	// nextStep: 'dog-mealtime'
	nextStep: 'dog-diet'
})

/**
 * Dog Diet Segment
 */
const segmentDogDiet: StepSegment = segment({
	name: 'dog-diet',
	slug: 'diet',
	uri: dogRoute('diet'),
	component: Diet,
	nextStep: 'plan-details'
})

/**
 * Plan Review Segment
 */
const segmentPlanReview: StepSegment = segment({
	name: 'plan-details',
	slug: 'plan',
	uri: dogRoute('plan'),
	nextStep: ({ data }: any) => {
		Logger.log('[planNextStepHandler] segmentPlanReview', data)
		if (data?.pageDogContext) {
			return 'dog-info'
		}
		return 'checkout'
	},
	terminate: true
})

/**
 * Plan Builder Segment
 */
const segmentPlanBuilder: StepSegment = segment({
	name: 'plan-builder',
	slug: 'build',
	uri: dogRoute('plan/build'),
	nextStep: (data: any) => {
		Logger.log('[planNextStepHandler] segmentPlanBuilder', data)
		if (data?.pageDogContext) {
			return 'dog-info'
		}
		return 'checkout'
	}
})

/**
 * Checkout Segment
 */
const segmentCheckout: StepSegment = segment({
	name: 'checkout',
	slug: 'checkout',
	uri: '/checkout',
	nextStep: 'complete'
})

const createSection = (
	name: string,
	label: string,
	segments: StepSegment[],
	repeater: string = ''
): StepSection => {
	const section: StepSection = {
		name,
		label,
		segments: segments.filter((segment) => segment.enabled)
	}
	repeater && (section.repeater = repeater)
	return section
}

export const onboardingSteps: StepSection[] = [
	createSection('user-info', 'Me', [segmentMyInfo, segmentMyDogs]),
	createSection(
		'my-dogs',
		'My Dogs',
		[segmentDogInfo, segmentDogBody, segmentDogActivity, segmentDogMealtime, segmentDogDiet],
		'dogs'
	),
	createSection('plan', 'Plan', [segmentPlanReview, segmentPlanBuilder]),
	createSection('checkout', 'Checkout', [segmentCheckout])
]

/**
 * Get Started Steps Hook
 * @returns {StepSection[]}
 */
const useOnboardingSteps = (): StepSection[] => {
	return onboardingSteps
}

export default useOnboardingSteps

export const segmentMapReducer = (steps: any[]) => {
	return steps.reduce((acc: any, section: any, sectionIndex: number): StepSection[] => {
		section.segments.forEach((segment: StepSection, segmentIndex: number) => {
			acc[segment.name as string] = {
				section: section.name,
				sectionIndex,
				segmentIndex,
				...segment
			}
		})
		return acc
	}, {})
}

/**
 * Segment Map
 */
export const segmentMap: { [name: string]: StepSegment } = segmentMapReducer(onboardingSteps)

/**
 * Get Next Step
 * @param {string} segment
 * @returns {StepSegment}
 */
export const getNextStep = (segment: string): StepSegment => {
	const { nextStep } = segmentMap[segment as any]
	return segmentMap[nextStep]
}

/**
 * Get Segment By Slug
 * @param {string} slug
 * @returns {StepSegment}
 */
export const getSegmentBySlug = (slug: string): StepSegment | undefined => {
	return Object.values(segmentMap).find((segment: any) => segment.slug === slug)
}

/**
 * Get Segment By Name
 * @param {string} name
 * @returns {StepSegment}
 */
export const getSegmentByName = (name: string): StepSegment => {
	return segmentMap[name as any]
}
